import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  getRestaurantLifecycle, cancelRestaurantService, reactivateRestaurantService,
  revokeRestaurantRetention, exportRestaurantDataset, getRestaurantExportManifest,
  downloadRestaurantData, downloadRestaurantArchive,
} from "../../services/supabase/restaurantLifecycleService";
import RestaurantContractReceipts from "./RestaurantContractReceipts";
import "../../styles/RestaurantLifecycle.css";

const date = value => value ? new Date(value).toLocaleString("es-AR", {
  timeZone: "America/Argentina/Buenos_Aires", dateStyle: "short", timeStyle: "short",
}) : "";
const datasets = {
  branches: "Sucursales", products: "Productos", ingredients: "Ingredientes",
  orders: "Pedidos de salon", order_items: "Detalle de pedidos de salon", payments: "Pagos de salon",
  external_customers: "Clientes de delivery", external_customer_addresses: "Direcciones de delivery",
  external_orders: "Pedidos externos", external_order_items: "Detalle de pedidos externos",
  external_order_payments: "Pagos de pedidos externos", menus: "Menus", categories: "Categorias",
  subcategories: "Subcategorias", restaurant_recipe_export_rows: "Recetas",
  restaurant_operational_export_rows: "Historial operativo disponible",
};
const taskNames = {
  personal_core: "Sesiones y contactos", personal_inventory_review: "Datos personales y registros operativos",
  external_providers: "Proveedores externos", backups: "Copias de seguridad",
  configuration_review: "Configuracion conservada",
};
const taskStates = {
  scheduled: "Programada", completed: "Completada", needs_review: "Pendiente de revision",
  failed: "Requiere atencion", cancelled: "Cancelada por reactivacion",
};

export default function RestaurantLifecyclePanel({ companyId, onChanged, onSignOut, showContract = false }) {
  const [state, setState] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirmation, setConfirmation] = useState(false);
  const [retain, setRetain] = useState(false);
  const [requestId, setRequestId] = useState(null);
  const [manifest, setManifest] = useState(null);
  const [dataset, setDataset] = useState("");
  const [progress, setProgress] = useState(null);
  const [archiveProgress, setArchiveProgress] = useState(null);
  const [exportResult, setExportResult] = useState("");
  const exportController = useRef(null);
  useEffect(() => () => exportController.current?.abort(), [companyId]);
  const load = useCallback(async () => {
    const next = await getRestaurantLifecycle(companyId);
    setState(next);
    if (next.isOwner && next.canExport) {
      const available = await getRestaurantExportManifest(companyId);
      setManifest(available);
      setDataset(previous => available.datasets.includes(previous) ? previous : available.datasets[0] || "");
    } else setManifest(null);
    return next;
  }, [companyId]);
  useEffect(() => { load().catch(e => setError(e.message)); }, [load]);
  const act = async (operation, refresh = true) => {
    setError(""); setBusy(true);
    try {
      await operation();
      if (refresh) {
        const next = await load();
        if (next.status === "active") { setRequestId(null); setConfirmation(false); setRetain(false); }
        if (onChanged) await onChanged();
      }
    } catch (e) { setError(e.name === "AbortError" ? "Descarga cancelada. No se entrego un archivo incompleto." : e.message || "No se pudo completar la gestion."); }
    finally { setBusy(false); setProgress(null); }
  };
  const cancel = () => act(async () => {
    const id = requestId || crypto.randomUUID();
    setRequestId(id);
    await cancelRestaurantService(companyId, id, retain, confirmation);
    setConfirmation(false); setRetain(false);
  });
  const download = () => act(async () => {
    const data = await exportRestaurantDataset(companyId, dataset, setProgress);
    downloadRestaurantData(data);
  }, false);
  const downloadArchive = () => act(async () => {
    setExportResult("");
    exportController.current = new AbortController();
    setArchiveProgress({ rows: 0, assets: 0 });
    try {
      const result = await downloadRestaurantArchive(companyId, setArchiveProgress, exportController.current.signal);
      setExportResult(`Archivo preparado: ${result.totalRows} registros y ${result.totalImages} imagenes. Revisa las exclusiones y los modulos no disponibles en el inventario.`);
    } finally { exportController.current = null; setArchiveProgress(null); }
  }, false);

  return <section className="restaurant-lifecycle" aria-labelledby="lifecycle-title">
    <h2 id="lifecycle-title">Baja y datos</h2>
    {error && <p role="alert" className="restaurant-lifecycle-error">{error}</p>}
    {!state && !error && <p role="status">Consultando el estado del servicio...</p>}
    {state && !state.isOwner && <p>El servicio de esta empresa esta dado de baja. El titular puede reactivarlo.</p>}
    {state?.isOwner && <>
      {state.status === "cancelled" ? <>
        <p><strong>Servicio dado de baja</strong> desde {date(state.cancelledAt)}.</p>
        <p>Constancia: {state.cancellationId}</p>
        <p>Consulta y descarga hasta {date(state.exportUntil)}. La reactivacion no tiene cargo y no elimina deudas anteriores.</p>
        <button type="button" disabled={busy} onClick={() => act(() => reactivateRestaurantService(companyId))}>
          Reactivar servicio
        </button>
        <p>Solo podran recuperarse los datos que no hayan sido eliminados. Menu, recetas y configuracion no personales: conservacion hasta {date(state.configurationUntil)}.</p>
        {state.retainPersonalData && <div className="restaurant-lifecycle-retention">
          <p>Autorizaste conservar datos personales hasta {date(state.personalDataUntil)}.</p>
          <button type="button" disabled={busy} onClick={() => act(() => revokeRestaurantRetention(companyId))}>Retirar autorizacion de conservacion</button>
        </div>}
      </> : <>
        <p>La baja alcanza a esta empresa y todas sus sucursales. No exige pagar antes las comisiones pendientes; esas deudas se mantienen.</p>
        {state.reactivatedAt && <p>Servicio reactivado. Las respuestas automaticas de WhatsApp quedaron pausadas; revisa y activa esa integracion antes de volver a usarla.</p>}
        {!state.enabled && <p>La baja desde la app todavia no esta habilitada. Podes solicitarla a <a href="mailto:mozzoappenterprise@gmail.com">mozzoappenterprise@gmail.com</a>.</p>}
        <fieldset disabled={busy || !state.enabled}>
          <legend>Solicitud de baja</legend>
          <label><input type="checkbox" checked={retain} onChange={e => setRetain(e.target.checked)} />
            Autorizo opcionalmente conservar los datos personales de esta empresa durante 12 meses desde la baja para una posible reactivacion. Puedo retirar esta autorizacion; no limita los derechos de sus titulares.
          </label>
          <label><input type="checkbox" checked={confirmation} onChange={e => setConfirmation(e.target.checked)} />
            Confirmo la baja de todas las sucursales. Entiendo que se detienen los nuevos pedidos y que tendre 30 dias para consultar y descargar.
          </label>
          <button type="button" className="restaurant-lifecycle-danger" disabled={!confirmation || busy} onClick={cancel}>Confirmar baja del servicio</button>
        </fieldset>
      </>}
      {manifest && <div className="restaurant-lifecycle-export">
        <h3>Descargar datos</h3>
        <p>Pedidos, clientes, menu, recetas, stock, movimientos y configuracion disponibles. Incluye imagenes guardadas en las fichas y un inventario de contenido y exclusiones. No incluye claves, respaldos ni archivos alojados en otros servicios.</p>
        <p>Los datos se consultan durante la descarga; no representan una copia de un unico instante. El archivo puede contener datos personales, fiscales y bancarios: guardalo en un lugar privado.</p>
        <button type="button" disabled={busy} onClick={downloadArchive}>Descargar archivo ZIP</button>
        {archiveProgress && <>
          <p role="status">Preparando {archiveProgress.rows} registros y {archiveProgress.assets} imagenes...</p>
          <button type="button" onClick={() => exportController.current?.abort()}>Cancelar descarga</button>
        </>}
        {exportResult && <p role="status">{exportResult}</p>}
        <h4>Descarga individual</h4>
        <label htmlFor="lifecycle-dataset">Informacion</label>
        <select id="lifecycle-dataset" value={dataset} disabled={busy} onChange={e => setDataset(e.target.value)}>
          {manifest.datasets.map(key => <option key={key} value={key}>{datasets[key] || key}</option>)}
        </select>
        <button type="button" disabled={busy || !dataset} onClick={download}>Descargar datos</button>
        {progress !== null && <p role="status">Preparando {progress} registros...</p>}
      </div>}
      {state.status === "cancelled" && !state.canExport && <p>Finalizo el plazo de descarga. Para ejercer derechos sobre datos personales, escribi a <a href="mailto:mozzoappenterprise@gmail.com">mozzoappenterprise@gmail.com</a>.</p>}
      {state.tasks?.length > 0 && <div className="restaurant-lifecycle-tasks"><h3>Conservacion y eliminacion</h3>
        <p>Los comprobantes y evidencias necesarios se conservan por su finalidad y plazo legal. Una tarea pendiente no significa que los datos ya fueron eliminados.</p>
        <ul>{state.tasks.map(task => <li key={task.type}>{taskNames[task.type] || task.type}: {taskStates[task.status] || task.status}. Fecha prevista: {date(task.dueAt)}.</li>)}</ul>
      </div>}
      {showContract && <RestaurantContractReceipts companyId={companyId} />}
    </>}
    {onSignOut && <button type="button" disabled={busy} onClick={onSignOut}>Cerrar sesion</button>}
  </section>;
}
